<template>
  <div>
    <!-- NAGŁÓWEK STRONY (wyśrodkowany) -->
    <header class="list-head is-centered">
      <h1 class="list-title">
        {{ pageTitle }}
      </h1>
      <!-- licznik ukryty w tej wersji -->
      <!-- <span class="list-count" v-if="!loading && products.length">
        {{ products.length }} produktów
      </span> -->
    </header>

    <!-- LISTA PRODUKTÓW (grid) -->
    <div class="products-grid">
      <AppProductCard
        v-for="p in products"
        :key="p.id"
        :id="p.id"
        :slug="p.slug"
        :product="p"
        :img="p.get_image"
        :name="p.name"
        :price="getPriceForCurrency(p)"
        :currency="getCurrencySymbol(currentCurrency.value)"
      />
    </div>


    <div v-if="loading">Ładowanie...</div>
    <div v-if="error">{{ errorMsg }}</div>
  </div>
</template>

<script>
import AppProductCard from './AppProductCard.vue'
import { currentLang } from '@/locales/lang'
import { currentCurrency } from '@/composables/currency'

const API = '/api/v1'

export default {
  name: 'AppProductList',
  components: { AppProductCard },
  data() {
    return {
      products: [],
      categories: [],
      categoryName: '',       // nazwa kategorii do H1

      loading: false,
      error: false,
      errorMsg: '',

      // drag/scroll dla raila
      dragging: false,
      dragStartX: 0,
      scrollStartLeft: 0,
      dragRef: null
    }
  },
  mounted() {
    this.loadProducts()
    this.loadCategories()

    window.addEventListener('languageChanged', this.reloadAll)
    window.addEventListener('currencyChanged', this.reloadAll)
  },
  beforeUnmount() {
    window.removeEventListener('languageChanged', this.reloadAll)
    window.removeEventListener('currencyChanged', this.reloadAll)
  },
  watch: {
    // zmiana kategorii w URL (np. /category/:slug)
    '$route.params.slug'() {
      this.loadProducts()
    }
  },
  computed: {
    currentCurrency() {
      return currentCurrency
    },
    pageTitle() {
      // gdy jesteśmy w /category/:slug – pokaż nazwę
      if (this.$route.params.slug) return this.categoryName || 'Kategoria'
      // gdy bez slug – lista najnowszych
      return 'Najnowsze produkty'
    }
  },
  methods: {
    reloadAll() {
      this.loadProducts()
      this.loadCategories()
    },

    async loadProducts() {
      this.loading = true
      this.error = false
      this.errorMsg = ''

      const slug = this.$route.params.slug

      try {
        if (slug) {
          // tryb: produkty w konkretnej kategorii
          const res = await fetch(`${API}/categories/${slug}/`, {
            headers: { 'Accept-Language': currentLang.value }
          })
          if (!res.ok) {
            const text = await res.text()
            throw new Error(`HTTP ${res.status} ${text.slice(0,150)}...`)
          }
          const category = await res.json()
          this.categoryName = category?.name || ''
          this.products = Array.isArray(category.products) ? category.products : []
        } else {
          // tryb: najnowsze produkty
          const res = await fetch(`${API}/latest-products/`, {
            headers: { 'Accept-Language': currentLang.value }
          })
          const data = await res.json()
          if (!res.ok) throw new Error('HTTP ' + res.status + ' ' + (data && JSON.stringify(data)))
          this.products = Array.isArray(data) ? data : []
          this.categoryName = ''
        }
      } catch (error) {
        this.error = true
        this.errorMsg = error.message
        console.error('loadProducts error:', error)
      } finally {
        this.loading = false
      }
    },

    async loadCategories() {
      try {
        const res = await fetch(`${API}/categories/`, {
          headers: { 'Accept-Language': currentLang.value }
        })
        const data = await res.json()
        if (!res.ok) throw new Error(`HTTP ${res.status} ${JSON.stringify(data)}`)
        this.categories = Array.isArray(data) ? data : (data.results || [])
      } catch (err) {
        console.error('Category rail load error:', err)
      }
    },

    resolveImage(cat) {
      return cat.get_image || cat.get_thumbnail || '/placeholder.png'
    },

    getPriceForCurrency(product) {
      const priceKey = {
        PLN: 'price_pln',
        EUR: 'price_eur',
        USD: 'price_usd',
        CZK: 'price_czk',
        HUF: 'price_huf',
        BGN: 'price_bgn',
        RON: 'price_ron',
        DKK: 'price_dkk',
        SEK: 'price_sek'
      }[currentCurrency.value] || 'price_pln'

      const priceNum = Number(product[priceKey])
      return isNaN(priceNum) ? '0.00' : priceNum.toFixed(2)
    },

    getCurrencySymbol(currencyCode) {
      const symbols = {
        PLN: 'zł',
        USD: '$',
        EUR: '€',
        CZK: 'Kč',
        HUF: 'Ft',
        BGN: 'лв',
        RON: 'lei',
        DKK: 'kr.',
        SEK: 'kr'
      }
      return symbols[currencyCode] || currencyCode
    },

    /* ------- rail: scroll + drag ------- */
    scrollRail(refName, dir) {
      const el = this.$refs[refName]
      if (!el) return
      const item = el.querySelector('.rail-item')
      const step = item ? item.offsetWidth + 16 : 300
      el.scrollBy({ left: dir * step * 2, behavior: 'smooth' })
    },
    onDragStart(e, refName) {
      const el = this.$refs[refName]
      if (!el) return
      this.dragging = true
      this.dragRef = el
      this.dragStartX = (e.touches ? e.touches[0].clientX : e.clientX)
      this.scrollStartLeft = el.scrollLeft
    },
    onDragMove(e) {
      if (!this.dragging || !this.dragRef) return
      const x = (e.touches ? e.touches[0].clientX : e.clientX)
      const delta = this.dragStartX - x
      this.dragRef.scrollLeft = this.scrollStartLeft + delta
    },
    onDragEnd() {
      this.dragging = false
      this.dragRef = null
    }
  }
}
</script>

<style>
/* ===== H1 + meta ===== */
.list-head{
  max-width: 1400px;
  margin: 10px auto 6px;
  padding: 0 12px;
  display:flex;
  align-items: baseline;
  gap: 12px;
}
.list-title{
  font-weight: 800;
  font-size: clamp(1.4rem, 2.4vw, 1.8rem);
  margin: 0;
}
.list-count{
  color: #666;
  font-weight: 600;
  font-size: .98rem;
}

/* Wyśrodkowanie tytułu (wariant z .is-centered) */
.list-head.is-centered{
  max-width: 1400px;
  margin: 10px auto 6px;
  padding: 0 12px;
  display: grid;
  grid-template-columns: 1fr auto 1fr; /* lewa | tytuł | prawa */
  align-items: center;
}
.list-head.is-centered .list-title{
  grid-column: 2;
  text-align: center;
  margin: 0;
}
.list-head.is-centered .list-count{ display: none !important; }

/* ===== RAIL: wspólne ===== */
.home-rail{
  max-width: 1400px;
  margin: 18px auto 26px;
  padding: 0 12px;
}
.rail-head{
  display:flex; align-items:center; justify-content:space-between;
  padding: 6px 4px 10px;
}
.rail-head h2{
  font-weight:800; font-size: clamp(1.2rem, 2.2vw, 1.6rem); margin:0;
}
.rail-actions{ display:flex; gap:8px; }

/* wyśrodkowany nagłówek raila */
.rail-head.is-centered{
  display: grid;
  grid-template-columns: 1fr auto 1fr;  /* lewa pusta | tytuł | strzałki */
  align-items: center;
  padding: 6px 4px 10px;
}
.rail-head.is-centered h2{
  grid-column: 2;
  text-align: center;
  margin: 0;
}
.rail-head.is-centered .rail-actions{
  grid-column: 3;
  justify-self: end;
}

.rail-arrow{
  width:38px; height:38px; border-radius:50%;
  background:#fff; border:1px solid #e8ecf5; cursor:pointer;
  box-shadow:0 2px 10px rgba(0,0,0,.06);
  font-size:20px; line-height:1; display:grid; place-items:center;
}
.rail-arrow:hover{ background:#f7f8fb; }

.rail{
  display:grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(220px, 280px);
  gap:16px;
  overflow-x:auto;
  padding: 6px 2px 4px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}
.rail::-webkit-scrollbar{ height: 0; }

.rail-item{
  scroll-snap-align: start;
  display:flex; flex-direction:column;
  background:#fff;
  border:1px solid #e9edf6;
  border-radius:14px;
  text-decoration:none; color:inherit;
  overflow:hidden;
  transition:transform .15s ease, box-shadow .15s ease, border-color .15s ease;
}
.rail-item:hover{
  transform: translateY(-2px);
  border-color:#dbe3f4;
  box-shadow: 0 8px 18px rgba(20,30,60,.05);
}
.ri-img{
  width:100%; aspect-ratio: 4/3;
  display:flex; align-items:center; justify-content:center;
  background:#fff; overflow:hidden;
}
.ri-img img{ width:100%; height:100%; object-fit:cover; }
.ri-title{
  padding:10px 12px 4px; font-weight:700; font-size:.98rem; line-height:1.3;
}
.ri-price{
  padding:0 12px 12px; font-weight:800; font-size:1rem;
}
</style>
